/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { LanguageService, NotifyService } from '../../../../../core/services';
import { LicenceModel } from '../../models';
import { LicenceTypesEnum } from '../../enums';
import { PlatformEnum } from '../../enums/platform.enum';

@Injectable()
export class LicenceService {
    /**
     * Store the licence pill message.
     */
    public licencePillMessage: string;

    /**
     * Stored licence object.
     * @private
     */
    private _licenceObj: LicenceModel;

    constructor(
        private languageService: LanguageService,
        private notifyService: NotifyService
    ) {

    }

    /**
     * Set the licence object in the service.
     * @param licence
     */
    set licenceObj(licence: LicenceModel) {
        if (licence) {
            this._licenceObj = licence;
            window.localStorage.setItem('licence', JSON.stringify(this._licenceObj));
            this.showLicenceWarning();
        }
    }

    /**
     * Get the licence object.
     */
    get licenceObj(): LicenceModel {
        return this._licenceObj;
    }

    /**
     * Show a static warning based on license data.
     */
    public showLicenceWarning(): void {
        this.notifyService.removeNotificationById('licenceNotification');

        const options = {
            hideTypeMessage: true,
            header: true,
            url: 'local-configuration/licence',
            id: 'licenceNotification'
        };

        const { message, warning, notificationType } = this.getLicenseWarningObject();

        if (!this.licenceObj.supportExpired || this.licenceObj.daysToExpire > 25) {
            this.notifyService.removeNotificationById('licenceNotification');
        }

        if (warning) {
            this.notifyService[notificationType](warning, options);
        }

        this.licencePillMessage = message;
    }

    /**
     * Set the appliance name in the top right-hand corner of the appliance.
     */
    public getApplianceModelName(): string {
        let platformString: string;
        // Some platforms don't match the model name
        switch (this.licenceObj.platform) {
            case PlatformEnum.VMWARE:
            case PlatformEnum.HYPERV:
            case PlatformEnum.KVM:
            case PlatformEnum.XEN:
            case PlatformEnum.NUTANIX:
                platformString = 'VA ';
                break;
            case PlatformEnum.AMAZON:
                platformString = 'AWS ';
                break;
            case PlatformEnum.SUPERMICRO:
            case PlatformEnum.DELL:
                platformString = '';
                break;
            case PlatformEnum.AZURE:
                platformString = 'Azure ';
                break;
            default:
                platformString = 'Unknown ';
        }

        return platformString + this.licenceObj.model?.toUpperCase();
    }

    /**
     * Get license warning object.
     * @private
     */
    private getLicenseWarningObject(): any {
        if (this.licenceObj.licenceType === LicenceTypesEnum.TRIAL) {
            return this.getTrialLicenseWarning();
        } else if (this.licenceObj.licenceType === LicenceTypesEnum.NFR) {
            return this.getNFRLicenseWarning();
        } else {
            return this.getFullLicenseWarning();
        }
    }

    /**
     * Get licence warning object for Trial licences.
     * @private
     */
    private getTrialLicenseWarning(): any {
        let message: string;
        let warning: string;
        let notificationType: string = 'warning';

        if (this.licenceObj.daysToExpire > 0) {
            message = `${this.languageService.translate('licence_support_expires_in') + this.licenceObj.daysToExpire} days.`;
            warning = this.languageService.translate('licence_expiration_due') + this.licenceObj.daysToExpire +
                ' days. ' + this.languageService.translate('licence_expired_guide');
        } else {
            message = `Your Support Agreement expired on ${this.licenceObj.supportExpireDate}.`;
            warning = 'Your Support Agreement has expired. ' + this.languageService.translate('licence_expired_guide');
            notificationType = 'error';
        }

        return { message, warning, notificationType };
    }

    /**
     * Get licence warning object for NFR licences.
     * @private
     */
    private getNFRLicenseWarning(): any {
        let message: string;
        let warning: string;
        let notificationType: string = 'warning';
        if (this.licenceObj.daysToExpire > 0) {
            message = 'NFR Appliance: Not intended for production deployments.';
            warning = 'NFR Appliance: Not intended for production deployments. You have '
                + this.licenceObj.daysToExpire + 'days remaining';
        } else {
            message = 'This NFR licence has expired. ';
            warning = message + this.languageService.translate('licence_contact_support');
            notificationType = 'error';
        }

        return {
            message, warning, notificationType
        };
    }

    /**
     * Get licence warning object for Full licences.
     * @private
     */
    private getFullLicenseWarning(): any {
        let message: string;
        let warning: string;
        let notificationType: string;

        if (this.licenceObj.daysToExpire === 0) {
            message = this.languageService.translate('license_expired');
            warning = this.languageService.translate('license_expired') + ' ' +
                this.languageService.translate('licence_contact_support');
            notificationType = 'error';
        } else if (this.licenceObj.daysToExpire < 25 && this.licenceObj.daysToExpire > 0) {
            message = `${this.languageService.translate('license_warn_remaining_days') + this.licenceObj.daysToExpire} days`;
            warning = this.languageService.translate('license_warn_remaining_days') +
                this.licenceObj.daysToExpire + ' days. ' + this.languageService.translate('license_update_license_guide');
            notificationType = 'warning';
        }

        return {
            message, warning, notificationType
        };
    }
}
